import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Clock, CheckCircle, XCircle, BookOpen, Languages, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TranslatorBookRequests = ({ onUpdate }) => {
  const { user } = useAuth();
  const [availableBooks, setAvailableBooks] = useState([]);
  const [myBook, setMyBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const fetchData = async () => {
    try {
      const [availableRes, myBookRes] = await Promise.all([
        axios.get(`${API}/translator/available-translation-books`),
        axios.get(`${API}/translator/my-translation-book`)
      ]);
      setAvailableBooks(availableRes.data);
      setMyBook(myBookRes.data && myBookRes.data.id ? myBookRes.data : null);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('opgeskort')) {
        toast.error('Jou rekening is opgeskort. Kontak die admin.');
      } else {
        toast.error('Kon nie data laai nie');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectBook = async () => {
    if (!selectedBook) return;
    
    setSelecting(true);
    try {
      await axios.post(`${API}/translator/select-translation-book/${selectedBook.id}`);
      toast.success('Boek suksesvol gekies! Jy kan nou begin vertaal.');
      setShowConfirmDialog(false);
      setSelectedBook(null);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek kies nie');
    } finally {
      setSelecting(false);
    }
  };

  const getDifficultyBadge = (level) => {
    const config = {
      'Easy': { label: 'Maklik', class: 'bg-green-50 text-green-600 border-green-200' },
      'Medium': { label: 'Medium', class: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
      'Hard': { label: 'Moeilik', class: 'bg-red-50 text-red-600 border-red-200' }
    };
    const c = config[level] || config['Medium'];
    return <Badge variant="outline" className={c.class}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="translator-book-requests">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Beskikbare Boeke
        </h2>
        <p className="text-gray-600">Kies 'n boek om te vertaal</p>
      </div>

      {/* Current Assignment */}
      {myBook && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <BookOpen className="h-5 w-5" />
              Jou Huidige Boek
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white rounded-lg">
                <Languages className="h-8 w-8 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{myBook.book_title}</h3>
                <p className="text-gray-600">deur {myBook.original_author}</p>
                <div className="flex gap-4 mt-2 text-sm text-gray-500">
                  <span>{myBook.original_language} → {myBook.target_language}</span>
                  <span>{myBook.page_count} bladsye</span>
                </div>
                {myBook.deadline_days && (
                  <p className="text-sm text-orange-600 mt-2">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Spertyd: {myBook.deadline_days} dae
                  </p>
                )}
              </div>
              <Badge className="bg-green-100 text-green-700">Besig</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notice if already has a book */}
      {myBook && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-amber-700">
                Jy het reeds 'n boek wat jy besig is om te vertaal. Voltooi dit eers voordat jy 'n nuwe boek kan kies.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Books */}
      <Card>
        <CardHeader>
          <CardTitle>Beskikbare Boeke vir Vertaling</CardTitle>
        </CardHeader>
        <CardContent>
          {availableBooks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">Geen boeke beskikbaar op die oomblik nie</p>
              <p className="text-sm">Kom later terug vir nuwe vertalingsgeleenthede</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {availableBooks.map((book) => (
                <div 
                  key={book.id} 
                  className="p-4 border rounded-lg hover:border-green-300 transition-colors"
                  data-testid={`available-book-${book.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{book.book_title}</h3>
                        {getDifficultyBadge(book.difficulty_level)}
                      </div>
                      <p className="text-gray-600">deur {book.original_author}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Languages className="h-4 w-4" />
                          {book.original_language} → {book.target_language}
                        </span>
                        <span>{book.page_count} bladsye</span>
                        {book.genre && <span>{book.genre}</span>}
                        {book.deadline_days && (
                          <span className="text-orange-600">
                            <Clock className="h-4 w-4 inline mr-1" />
                            {book.deadline_days} dae
                          </span>
                        )}
                      </div>
                      {book.description && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{book.description}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => { setSelectedBook(book); setShowConfirmDialog(true); }}
                      className="bg-green-600 hover:bg-green-700"
                      disabled={!!myBook}
                      data-testid={`select-book-${book.id}`}
                    >
                      Kies Hierdie Boek
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bevestig Boek Keuse</DialogTitle>
          </DialogHeader>
          {selectedBook && (
            <div className="py-4">
              <div className="p-4 bg-gray-50 rounded-lg mb-4">
                <h3 className="font-semibold text-lg">{selectedBook.book_title}</h3>
                <p className="text-gray-600">deur {selectedBook.original_author}</p>
                <div className="flex gap-4 mt-2 text-sm text-gray-500">
                  <span>{selectedBook.original_language} → {selectedBook.target_language}</span>
                  <span>{selectedBook.page_count} bladsye</span>
                </div>
                {selectedBook.deadline_days && (
                  <p className="text-sm text-orange-600 mt-2">
                    Spertyd: {selectedBook.deadline_days} dae
                  </p>
                )}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="text-amber-800">
                  <strong>Let wel:</strong> Sodra jy hierdie boek kies, sal jy nie 'n ander boek kan kies totdat 
                  hierdie een voltooi is nie.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowConfirmDialog(false); setSelectedBook(null); }}
            >
              Kanselleer
            </Button>
            <Button 
              onClick={handleSelectBook}
              className="bg-green-600 hover:bg-green-700"
              disabled={selecting}
            >
              {selecting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Besig...</>
              ) : (
                'Bevestig Keuse'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TranslatorBookRequests;
